import { API_CONFIG } from './api/config';
import { httpService } from './api/http';
import { normalizeBandRouteHandle } from '../utils/bandRoutes';
import {
  normalizeUserConnectionList,
  normalizeUserConnectionPage,
  withOptionalOffsetPagination,
} from '../utils/userConnectionList';

const BANDS_ENDPOINT = `${API_CONFIG.BASE_URL}/api/bands`;
const CANONICAL_UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

const requireCanonicalUuid = (value, fieldName = 'objectId', uuidV4Only = false) => {
  if (typeof value !== 'string') {
    throw new Error(`${fieldName} must be a canonical UUID`);
  }
  const normalized = value.trim();
  const pattern = uuidV4Only ? UUID_V4_REGEX : CANONICAL_UUID_REGEX;
  if (!pattern.test(normalized)) {
    throw new Error(`${fieldName} must be a canonical UUID`);
  }
  return normalized;
};

const buildBandUrl = (bandObjectId, suffix = '') =>
  `${BANDS_ENDPOINT}/${encodeURIComponent(requireCanonicalUuid(bandObjectId, 'bandObjectId'))}${suffix}`;
const buildProfileUserBandsUrl = (userId) =>
  `${BANDS_ENDPOINT}/users/${encodeURIComponent(requireCanonicalUuid(userId, 'userId', true))}`;
const buildPublicProfileUserBandsUrl = (userId) =>
  `${BANDS_ENDPOINT}/public/users/${encodeURIComponent(requireCanonicalUuid(userId, 'userId', true))}`;
const buildBandByHandleUrl = (handle) => {
  if (typeof handle !== 'string' || !handle.trim()) {
    throw new Error('band handle is required to fetch band details');
  }
  const canonicalHandle = normalizeBandRouteHandle(handle);
  if (!canonicalHandle) {
    throw new Error('band handle must be canonical');
  }
  const params = new URLSearchParams();
  params.set('handle', canonicalHandle);
  return `${BANDS_ENDPOINT}/by-handle?${params.toString()}`;
};
const buildPublicBandByHandleUrl = (handle) => {
  if (typeof handle !== 'string' || !handle.trim()) {
    throw new Error('band handle is required to fetch public band details');
  }
  const canonicalHandle = normalizeBandRouteHandle(handle);
  if (!canonicalHandle) {
    throw new Error('band handle must be canonical');
  }
  const params = new URLSearchParams();
  params.set('handle', canonicalHandle);
  return `${BANDS_ENDPOINT}/public/by-handle?${params.toString()}`;
};
const buildAnonymousReadOptions = (options = {}) => ({
  ...options,
  credentials: 'omit',
  skipAuthRefresh: true,
});
const normalizePage = (response, options = {}) => {
  if (Array.isArray(response)) {
    return {
      items: response,
      hasMore: false,
      limit: null,
      offset: typeof options.offset === 'number' ? Math.max(0, Math.floor(options.offset)) : 0,
      nextOffset: null,
    };
  }
  const items = Array.isArray(response?.items) ? response.items : [];
  return {
    items,
    hasMore: response?.hasMore === true,
    limit: typeof response?.limit === 'number' ? response.limit : null,
    offset: typeof response?.offset === 'number'
      ? response.offset
      : (typeof options.offset === 'number' ? Math.max(0, Math.floor(options.offset)) : 0),
    nextOffset: typeof response?.nextOffset === 'number' ? response.nextOffset : null,
  };
};
const normalizeVisibility = (visibility) => {
  if (!visibility) {
    throw new Error('visibility is required to update band visibility');
  }
  const normalized = String(visibility).trim().toUpperCase();
  if (!normalized) {
    throw new Error('visibility is required to update band visibility');
  }
  if (normalized !== 'PUBLIC' && normalized !== 'PRIVATE') {
    throw new Error('visibility must be PUBLIC or PRIVATE');
  }
  return normalized;
};

const getBandFollowersPage = async (bandObjectId, options = {}) => {
  if (!bandObjectId) {
    throw new Error('bandObjectId is required to load band followers');
  }
  try {
    const data = await httpService.get(
      withOptionalOffsetPagination(buildBandUrl(bandObjectId, '/followers'), options),
      options
    );
    return normalizeUserConnectionPage(data, options);
  } catch (error) {
    console.error('Failed to fetch band followers', error);
    throw error;
  }
};

const getBandFollowers = async (bandObjectId, options = {}) => {
  const page = await getBandFollowersPage(bandObjectId, options);
  return normalizeUserConnectionList(page.items);
};

export const bandService = {
  /**
   * Fetches the bands the current user belongs to.
   * Falls back to an empty list if the API returns a non-array payload.
   */
  async listMyBands(options = {}) {
    try {
      const data = await httpService.get(BANDS_ENDPOINT, options);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch bands', error);
      throw error;
    }
  },

  async listUserBands(userId, options = {}) {
    try {
      const data = await httpService.get(buildProfileUserBandsUrl(userId), options);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch visible profile bands', error);
      throw error;
    }
  },

  async listPublicUserBands(userId, options = {}) {
    try {
      const data = await httpService.get(
        buildPublicProfileUserBandsUrl(userId),
        buildAnonymousReadOptions(options)
      );
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch public profile bands', error);
      throw error;
    }
  },

  /**
   * Discovery feed: every PUBLIC, non-deleted band, regardless of membership.
   * Powers the BandsWelcomePage. Falls back to [] on non-array responses so
   * the welcome view always has something safe to render.
   */
  async listPublicBands(options = {}) {
    try {
      const data = await httpService.get(`${BANDS_ENDPOINT}/public`, options);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch public bands', error);
      throw error;
    }
  },

  async listPublicBandsPage(options = {}) {
    try {
      const data = await httpService.get(
        withOptionalOffsetPagination(`${BANDS_ENDPOINT}/public`, options),
        options
      );
      return normalizePage(data, options);
    } catch (error) {
      console.error('Failed to fetch public bands page', error);
      throw error;
    }
  },

  async getBandDetails(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to fetch band details');
    }
    try {
      return await httpService.get(buildBandUrl(bandObjectId), options);
    } catch (error) {
      console.error('Failed to fetch band details', error);
      throw error;
    }
  },

  async getBandByHandle(handle, options = {}) {
    try {
      return await httpService.get(buildBandByHandleUrl(handle), options);
    } catch (error) {
      console.error('Failed to fetch band by handle', error);
      throw error;
    }
  },

  async getPublicBandByHandle(handle, options = {}) {
    try {
      return await httpService.get(buildPublicBandByHandleUrl(handle), options);
    } catch (error) {
      console.error('Failed to fetch public band by handle', error);
      throw error;
    }
  },

  async createBand(payload, options = {}) {
    if (!payload || typeof payload.name !== 'string' || payload.name.trim().length < 3) {
      throw new Error('Band name must be at least 3 characters');
    }
    const nextPayload = {
      name: payload.name.trim(),
      description: (payload.description || '').trim() || null,
    };
    if (payload.visibility !== undefined && payload.visibility !== null && String(payload.visibility).trim()) {
      nextPayload.visibility = normalizeVisibility(payload.visibility);
    }
    try {
      return await httpService.post(BANDS_ENDPOINT, nextPayload, options);
    } catch (error) {
      console.error('Failed to create band', error);
      throw error;
    }
  },

  async updateBand(bandObjectId, payload, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to update a band');
    }
    if (!payload || typeof payload !== 'object') {
      throw new Error('band payload is required to update a band');
    }
    try {
      return await httpService.put(buildBandUrl(bandObjectId), payload, options);
    } catch (error) {
      console.error('Failed to update band', error);
      throw error;
    }
  },

  async updateBandName(bandObjectId, name, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to update band name');
    }
    if (typeof name !== 'string') {
      throw new Error('name is required to update band name');
    }
    const trimmedName = name.trim();
    if (trimmedName.length < 3) {
      throw new Error('Band name must be at least 3 characters');
    }
    if (trimmedName.length > 128) {
      throw new Error('Band name must be 128 characters or fewer');
    }
    try {
      return await httpService.put(buildBandUrl(bandObjectId), { name: trimmedName }, options);
    } catch (error) {
      console.error('Failed to update band name', error);
      throw error;
    }
  },

  async deleteBand(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to delete a band');
    }
    try {
      return await httpService.delete(buildBandUrl(bandObjectId), options);
    } catch (error) {
      console.error('Failed to delete band', error);
      throw error;
    }
  },

  async updateBandVisibility(bandObjectId, visibility, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to update band visibility');
    }
    const normalized = normalizeVisibility(visibility);
    try {
      return await httpService.patch(buildBandUrl(bandObjectId, '/visibility'), { visibility: normalized }, options);
    } catch (error) {
      console.error('Failed to update band visibility', error);
      throw error;
    }
  },

  async updateBandLocation(bandObjectId, payload, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to update band location');
    }
    if (!payload || typeof payload !== 'object') {
      throw new Error('location payload is required to update band location');
    }
    try {
      return await httpService.patch(buildBandUrl(bandObjectId, '/location'), payload, options);
    } catch (error) {
      console.error('Failed to update band location', error);
      throw error;
    }
  },

  async inviteMember(bandObjectId, userId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to invite a member');
    }
    const normalizedUserId = requireCanonicalUuid(userId, 'userId', true);
    try {
      return await httpService.post(buildBandUrl(bandObjectId, '/members'), { userId: normalizedUserId }, options);
    } catch (error) {
      console.error('Failed to invite band member', error);
      throw error;
    }
  },

  async removeMember(bandObjectId, userId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to remove a member');
    }
    const normalizedUserId = requireCanonicalUuid(userId, 'userId', true);
    try {
      return await httpService.delete(buildBandUrl(bandObjectId, `/members/${encodeURIComponent(normalizedUserId)}`), options);
    } catch (error) {
      console.error('Failed to remove band member', error);
      throw error;
    }
  },

  async leaveBand(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to leave a band');
    }
    try {
      return await httpService.delete(buildBandUrl(bandObjectId, '/members/me'), options);
    } catch (error) {
      console.error('Failed to leave band', error);
      throw error;
    }
  },

  async acceptInvitation(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to accept an invitation');
    }
    try {
      return await httpService.post(buildBandUrl(bandObjectId, '/members/me/accept'), null, options);
    } catch (error) {
      console.error('Failed to accept band invitation', error);
      throw error;
    }
  },

  async rejectInvitation(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to reject an invitation');
    }
    try {
      return await httpService.post(buildBandUrl(bandObjectId, '/members/me/reject'), null, options);
    } catch (error) {
      console.error('Failed to reject band invitation', error);
      throw error;
    }
  },

  async getBandFollowStatus(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to load band follow status');
    }
    try {
      return await httpService.get(buildBandUrl(bandObjectId, '/followers/me'), options);
    } catch (error) {
      console.error('Failed to fetch band follow status', error);
      throw error;
    }
  },

  getBandFollowersPage,
  getBandFollowers,

  async followBand(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to follow a band');
    }
    try {
      return await httpService.post(buildBandUrl(bandObjectId, '/followers/me'), null, options);
    } catch (error) {
      console.error('Failed to follow band', error);
      throw error;
    }
  },

  async unfollowBand(bandObjectId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to unfollow a band');
    }
    try {
      return await httpService.delete(buildBandUrl(bandObjectId, '/followers/me'), options);
    } catch (error) {
      console.error('Failed to unfollow band', error);
      throw error;
    }
  },

  async deleteActivityEntry(bandObjectId, activityId, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to delete an activity entry');
    }
    if (!activityId) {
      throw new Error('activityId is required to delete an activity entry');
    }
    try {
      return await httpService.delete(
        buildBandUrl(
          bandObjectId,
          `/activity/${encodeURIComponent(activityId)}`
        ),
        options
      );
    } catch (error) {
      console.error('Failed to delete band activity entry', error);
      throw error;
    }
  },

  async updateMemberMainInstrument(bandObjectId, userId, mainInstrument, options = {}) {
    if (!bandObjectId) {
      throw new Error('bandObjectId is required to update member main instrument');
    }
    const normalizedUserId = requireCanonicalUuid(userId, 'userId', true);
    if (mainInstrument != null && typeof mainInstrument !== 'string') {
      throw new Error('mainInstrument must be a string or null');
    }
    try {
      return await httpService.patch(
        buildBandUrl(bandObjectId, `/members/${encodeURIComponent(normalizedUserId)}/main-instrument`),
        { mainInstrument: mainInstrument ?? null },
        options
      );
    } catch (error) {
      console.error('Failed to update member main instrument', error);
      throw error;
    }
  },
};

export default bandService;
